import { FC, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Text,
  FormControl,
  FormErrorMessage,
  HStack,
  Heading,
  Image,
  Input,
  Select as ChakraSelect,
  VStack,
  IconButton,
} from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import GoogleMapReact from 'google-map-react';

import { ReactComponent as TrashIcon } from '../../../assets/icons/trash.svg';
import UploadIcon from '../../../assets/icons/upload.svg';
import { ProjectForm } from '../models';
import { CenteredSpinner } from '../../../components';
import {
  calculateFileSize,
  formatDate,
  countriesList,
  nullifyEmptyInputs,
  transformDate,
} from '../../../utils';
import {
  useGetProjectByIdQuery,
  useCreateProjectMutation,
  useUpdateProjectMutation,
  displayNotification,
} from '../../../services';
import { Phase, Project, ProjectType } from '../../../models';
import { NewPhase } from '../components/';
import {
  isMutationSuccess,
  notifyMutationFailure,
} from '../../../utils/mutation-feedback';

const newPhase = {
  phaseName: '',
  phaseEstimatedEndDate: undefined,
  phaseStartDate: undefined,
};

export const SavedProject: FC = () => {
  const [projectLocation, setProjectLocation] = useState({
    lat: 30.0444,
    lng: 31.2357,
  });
  const [isPhasesFormShown, setIsPhasesFormShown] = useState(false);
  const [projectImage, setProjectImage] = useState({ name: '', size: 0 });

  const params = useParams();
  const { projectId } = params;
  const type = params.type || 'all';

  const { data: project, isLoading } = useGetProjectByIdQuery(
    Number(projectId),
    {
      skip: !projectId,
    }
  );

  const projectTypeEnum =
    type === 'single-phase' ? ProjectType.SINGLE : ProjectType.MULTI;

  const [isSinglePhaseProject, setIsSinglePhaseProject] = useState(
    projectTypeEnum === ProjectType.SINGLE
  );

  const navigate = useNavigate();

  const [createProject] = useCreateProjectMutation();
  const [updateProject] = useUpdateProjectMutation();

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
    resetField,
    control,
    reset,
    trigger,
    watch,
  } = useForm<ProjectForm>({
    defaultValues: {
      phases: [newPhase],
    },
  });

  const navigateToPreviousPage = () => navigate(`/projects/${type}`);

  async function onSubmit(data: ProjectForm) {
    const {
      projectName,
      projectImage,
      startDate,
      addressDetails,
      area,
      city,
      projectCode,
      country,
      endDate,
      landMark,
      phases,
    } = data;
    const formData = new FormData();
    if (projectImage && projectImage[0])
      formData.append('image', projectImage[0]);

    const projectInfo: Partial<Project> = {
      projectName,
      projectCode,
      startDate: startDate ? formatDate(startDate) : undefined,
      endDate: endDate ? formatDate(endDate) : endDate,
      projectLocation,
      country,
      city,
      area,
      landMark,
      addressDetails,
      projectTypeEnum,
    };

    if (projectInfo.projectTypeEnum === ProjectType.MULTI)
      projectInfo.phases = phases.map(phase => ({
        ...phase,
        phaseEstimatedEndDate: formatDate(phase.phaseEstimatedEndDate),
        phaseStartDate: formatDate(phase.phaseStartDate),
      })) as Phase[];

    if (projectId) projectInfo.id = Number(projectId);

    formData.append('project', JSON.stringify(nullifyEmptyInputs(projectInfo)));

    try {
      // Neither trigger is unwrapped: a rejected save resolved normally and
      // the page navigated back to the list, discarding the whole form —
      // including any phases just entered.
      const response = projectId
        ? await updateProject(formData)
        : await createProject(formData);

      if (!isMutationSuccess(response)) {
        notifyMutationFailure(response, {
          title: projectId
            ? 'Could not update this project'
            : 'Could not create the project',
          description:
            'Your changes were not saved. Please review the form and try again.',
        });
        return;
      }

      navigateToPreviousPage();
    } catch (error) {
      console.error(error);
      displayNotification({
        title: 'Something went wrong!',
        description:
          'Your changes were not saved. Please try this action later or contact BrickLynk support.',
        status: 'error',
      });
    }
  }

  useEffect(() => {
    if (project?.data) {
      const {
        projectName,
        projectImage,
        calculatedStartDate,
        calculatedEndDate,
        addressDetails,
        area,
        city,
        country,
        landMark,
        projectCode,
        projectLocation,
        projectImageFileName,
        projectImageSize,
        projectTypeEnum,
        phases,
      } = project.data;

      if (projectImageFileName) {
        setProjectImage({
          name: projectImageFileName.split('/').at(-1),
          size: Number(projectImageSize),
        });
      }

      const savedProject: Partial<Project> = {
        projectName,
        projectImage,
        startDate: transformDate(calculatedStartDate),
        endDate: transformDate(calculatedEndDate),
        addressDetails,
        area,
        city,
        country,
        landMark,
        projectCode,
        projectLocation,
      };

      if (projectTypeEnum === ProjectType.MULTI) {
        savedProject.phases = phases.map(phase => ({
          ...phase,
          phaseEstimatedEndDate: transformDate(phase.phaseEstimatedEndDate),
          phaseStartDate: transformDate(phase.phaseStartDate),
        }));

        setIsSinglePhaseProject(false);
      }

      reset(savedProject);
    }
  }, [project?.data, isLoading, reset]);

  if (isLoading) return <CenteredSpinner />;

  return (
    <>
      {isPhasesFormShown ? (
        <NewPhase
          control={control}
          errors={errors}
          isLoading={isSubmitting}
          register={register}
          handleAddPhase={isAdded => {
            setIsPhasesFormShown(isAdded);
            if (isAdded) void handleSubmit(onSubmit)();
          }}
        />
      ) : (
        <VStack align="stretch">
          <HStack justify="space-between" mt="4" align="center">
            <Heading color="custom_blue.1" fontSize="2xl">
              Basic Information
              {isSinglePhaseProject
                ? ' (Single Phase Project)'
                : ' (Multiple Phase Project)'}
            </Heading>

            {isSinglePhaseProject && (
              <HStack mt={4} justify="end">
                <Button onClick={navigateToPreviousPage}>Cancel</Button>
                <Button
                  isLoading={isSubmitting}
                  type="button"
                  onClick={handleSubmit(onSubmit)}
                  bg="custom_orange.2"
                  _hover={{ bg: 'custom_orange.2' }}
                  color="white"
                >
                  Save
                </Button>
              </HStack>
            )}
          </HStack>

          <VStack as="form" align="stretch" mb="4" mt="7">
            <Box>
              <VStack
                align="start"
                w="full"
                p="4"
                borderRadius="md"
                border="1px"
                borderColor="custom_gray.3"
                gap="4"
                mb="5"
                bg="white"
              >
                <HStack gap="4" align="flex-start" w="full">
                  <VStack color="custom_blue.1" align="start" flexBasis="25%">
                    <Box fontSize="sm" fontWeight="semibold">
                      Project Name
                    </Box>
                  </VStack>
                  <FormControl
                    isInvalid={!!errors.projectName}
                    flexBasis="50%"
                    mt="2"
                  >
                    <Input
                      placeholder="Project Name"
                      {...register('projectName', {
                        required: 'Project Name is required',
                      })}
                    />
                    <FormErrorMessage>
                      {errors.projectName && errors.projectName.message}
                    </FormErrorMessage>
                  </FormControl>
                </HStack>

                <HStack gap="4" align="flex-start" w="full">
                  <VStack color="custom_blue.1" align="start" flexBasis="25%">
                    <Box fontSize="sm" fontWeight="semibold">
                      Project Image{' '}
                      <Box fontSize="xs" as="span">
                        (Optional)
                      </Box>
                    </Box>
                  </VStack>

                  <Box flexBasis="50%">
                    <Box>
                      <Input
                        display="none"
                        id="projectImage"
                        onInput={e => {
                          const files = (e.target as HTMLInputElement).files;
                          if (!files) return;
                          const [file] = files;
                          const { name, size } = file;

                          setProjectImage({ name, size });
                        }}
                        type="file"
                        accept=".png, .jpg"
                        {...register('projectImage')}
                      />

                      <label htmlFor="projectImage">
                        <VStack
                          cursor="pointer"
                          border="2px"
                          borderRadius="md"
                          borderStyle="dashed"
                          borderColor="custom_orange.2"
                          py="3"
                        >
                          <Box
                            border="1px"
                            borderColor="custom_gray.4"
                            borderRadius="md"
                            p="2"
                          >
                            <Image src={UploadIcon} />
                          </Box>

                          <Text color="custom_blue.1" fontSize="sm">
                            <Box
                              as="span"
                              color="custom_orange.2"
                              fontWeight="semibold"
                            >
                              Click to upload
                            </Box>{' '}
                            or drag and drop
                          </Text>
                          <Text color="custom_blue.1" fontSize="xs">
                            PNG, JPG
                          </Text>
                        </VStack>
                      </label>

                      {projectImage.name && (
                        <VStack
                          align="start"
                          border="1px"
                          borderRadius="md"
                          borderColor="custom_gray.4"
                          p="3"
                          mt="3"
                        >
                          <HStack justify="space-between" w="full">
                            <Box
                              fontWeight="medium"
                              color="custom_blue.1"
                              fontSize="sm"
                            >
                              {projectImage.name}
                            </Box>

                            <IconButton
                              icon={<TrashIcon />}
                              aria-label="delete uploaded company logo"
                              bg="transparent"
                              onClick={() => {
                                setProjectImage({ name: '', size: 0 });
                                resetField('projectImage', {
                                  keepDirty: true,
                                  keepError: true,
                                  keepTouched: true,
                                });
                              }}
                            />
                          </HStack>
                          <Box color="custom_blue.1" fontSize="sm">
                            {calculateFileSize(projectImage.size)} MB
                          </Box>
                        </VStack>
                      )}
                    </Box>

                    <FormErrorMessage>
                      {errors.projectImage && errors.projectImage.message}
                    </FormErrorMessage>
                  </Box>
                </HStack>

                <HStack gap="4" align="flex-start" w="full">
                  <VStack color="custom_blue.1" align="start" flexBasis="25%">
                    <Box fontSize="sm" fontWeight="semibold">
                      Project Code{' '}
                      <Box fontSize="xs" as="span">
                        (Optional)
                      </Box>
                    </Box>
                  </VStack>
                  <FormControl
                    isInvalid={!!errors.projectCode}
                    flexBasis="50%"
                    mt="2"
                  >
                    <Input
                      placeholder="Project Code"
                      {...register('projectCode')}
                    />
                    <FormErrorMessage>
                      {errors.projectCode && errors.projectCode.message}
                    </FormErrorMessage>
                  </FormControl>
                </HStack>

                {isSinglePhaseProject && (
                  <>
                    <HStack gap="4" align="flex-start" w="full">
                      <VStack
                        color="custom_blue.1"
                        align="start"
                        flexBasis="25%"
                      >
                        <Box fontSize="sm" fontWeight="semibold">
                          Start Date
                        </Box>
                      </VStack>
                      <FormControl
                        isInvalid={!!errors.startDate}
                        flexBasis="50%"
                        mt="2"
                      >
                        <Input
                          {...register('startDate', {
                            required: isSinglePhaseProject
                              ? 'Start Date is required'
                              : undefined,
                          })}
                          type="date"
                        />
                        <FormErrorMessage>
                          {errors.startDate && errors.startDate.message}
                        </FormErrorMessage>
                      </FormControl>
                    </HStack>

                    <HStack gap="4" align="flex-start" w="full">
                      <VStack
                        color="custom_blue.1"
                        align="start"
                        flexBasis="25%"
                      >
                        <Box fontSize="sm" fontWeight="semibold">
                          End Date{' '}
                        </Box>
                      </VStack>
                      <FormControl
                        isInvalid={!!errors.endDate}
                        flexBasis="50%"
                        mt="2"
                      >
                        <Input
                          {...register('endDate', {
                            required: isSinglePhaseProject
                              ? 'End Date is required'
                              : undefined,
                          })}
                          type="date"
                        />
                        <FormErrorMessage>
                          {errors.endDate && errors.endDate.message}
                        </FormErrorMessage>
                      </FormControl>
                    </HStack>
                  </>
                )}
              </VStack>

              <VStack
                align="start"
                w="full"
                p="4"
                borderRadius="md"
                border="1px"
                borderColor="custom_gray.3"
                gap="4"
                bg="white"
              >
                <HStack gap="4" align="flex-start" w="full">
                  <VStack color="custom_blue.1" align="start" flexBasis="25%">
                    <Box fontSize="sm" fontWeight="semibold">
                      Project Location{' '}
                      <Box fontSize="xs" as="span">
                        (Optional)
                      </Box>
                    </Box>
                  </VStack>
                  <VStack flexBasis="50%" mt="2">
                    <FormControl>
                      <Input
                        placeholder="Search Your Project Location"
                        {...register('landMark')}
                      />
                    </FormControl>

                    <Box w="full" h="60">
                      <GoogleMapReact
                        bootstrapURLKeys={{
                          key: import.meta.env.GOOGLE_MAP_API_KEY,
                        }}
                        defaultCenter={projectLocation}
                        defaultZoom={10}
                        yesIWantToUseGoogleMapApiInternals
                        onClick={({ lat, lng }) =>
                          setProjectLocation({ lat, lng })
                        }
                      >
                        <div {...projectLocation}>My Marker</div>
                      </GoogleMapReact>
                    </Box>
                  </VStack>
                </HStack>

                <HStack gap="4" align="flex-start" w="full">
                  <VStack color="custom_blue.1" align="start" flexBasis="25%">
                    <Box fontSize="sm" fontWeight="semibold">
                      Country{' '}
                      <Box fontSize="xs" as="span">
                        (Optional)
                      </Box>
                    </Box>
                  </VStack>
                  <FormControl flexBasis="50%" mt="2">
                    <ChakraSelect {...register('country')}>
                      <option />
                      {Object.keys(countriesList).map(countryName => (
                        <option key={countryName} value={countryName}>
                          {countryName}
                        </option>
                      ))}
                    </ChakraSelect>
                  </FormControl>
                </HStack>

                <HStack gap="4" align="flex-start" w="full">
                  <VStack color="custom_blue.1" align="start" flexBasis="25%">
                    <Box fontSize="sm" fontWeight="semibold">
                      City{' '}
                      <Box fontSize="xs" as="span">
                        (Optional)
                      </Box>
                    </Box>
                  </VStack>
                  <FormControl flexBasis="50%" mt="2">
                    <ChakraSelect {...register('city')}>
                      <option />
                      {countriesList[
                        watch('country') as keyof typeof countriesList
                      ]?.map(countryName => (
                        <option key={countryName} value={countryName}>
                          {countryName}
                        </option>
                      ))}
                    </ChakraSelect>
                  </FormControl>
                </HStack>

                <HStack gap="4" align="flex-start" w="full">
                  <VStack color="custom_blue.1" align="start" flexBasis="25%">
                    <Box fontSize="sm" fontWeight="semibold">
                      Area{' '}
                      <Box fontSize="xs" as="span">
                        (Optional)
                      </Box>
                    </Box>
                  </VStack>
                  <FormControl flexBasis="50%" mt="2">
                    <Input placeholder="Area" {...register('area')} />
                  </FormControl>
                </HStack>

                <HStack gap="4" align="flex-start" w="full">
                  <VStack color="custom_blue.1" align="start" flexBasis="25%">
                    <Box fontSize="sm" fontWeight="semibold">
                      Land Mark{' '}
                      <Box fontSize="xs" as="span">
                        (Optional)
                      </Box>
                    </Box>
                  </VStack>
                  <FormControl flexBasis="50%" mt="2">
                    <Input placeholder="Land Mark" {...register('landMark')} />
                  </FormControl>
                </HStack>

                <HStack gap="4" align="flex-start" w="full">
                  <VStack color="custom_blue.1" align="start" flexBasis="25%">
                    <Box fontSize="sm" fontWeight="semibold">
                      Address Details{' '}
                      <Box fontSize="xs" as="span">
                        (Optional)
                      </Box>
                    </Box>
                  </VStack>
                  <FormControl flexBasis="50%" mt="2">
                    <Input
                      placeholder="Address Details"
                      {...register('addressDetails')}
                    />
                  </FormControl>
                </HStack>
              </VStack>
            </Box>
            {!isSinglePhaseProject && (
              <HStack mt={4} justify="end">
                <Button
                  onClick={() => {
                    void trigger(['projectName']).then(isValid => {
                      if (isValid) setIsPhasesFormShown(true);
                    });
                  }}
                >
                  <Text>Next</Text>
                </Button>
              </HStack>
            )}
          </VStack>
        </VStack>
      )}
    </>
  );
};
